import React, { useState, useEffect, useRef, useCallback, useMemo } from 'react';
import { createPortal } from 'react-dom';
import {
    X, Phone, PhoneCall, Train, Plane, Bus, MapPin, Calendar,
    CheckCircle2, Loader2, RotateCcw, ArrowUpDown, Copy, Check, AlertCircle, Clock,
} from 'lucide-react';
import {
    SUPPORT_PHONE,
    SUPPORT_PHONE_DISPLAY,
    TRAIN_CLASS_OPTIONS,
    FLIGHT_CLASS_OPTIONS,
    TRAIN_CLASS_VALUES,
    FLIGHT_CLASS_VALUES,
    MODE_OPTIONS,
    COUNTRY_CODES,
    DEFAULT_COUNTRY_CODE,
} from '../../config/contactConfig';
import { FEATURES } from '../../config/features';
import { getCallStatus, formatCloseTime, describeNextOpen } from '../../utils/callHours';
import stationsData from '../../data/stations.json';
import airportsData from '../../data/airports.json';

// ───────────────────────────────────────────────────────────────────────────
// Helpers
// ───────────────────────────────────────────────────────────────────────────
const MODE_ICONS = { Train, Flight: Plane, Bus };

const toISODate = (d) => {
    if (!d) return '';
    const dt = d instanceof Date ? d : new Date(d);
    if (isNaN(dt)) return '';
    return `${dt.getFullYear()}-${String(dt.getMonth() + 1).padStart(2, '0')}-${String(dt.getDate()).padStart(2, '0')}`;
};

const todayISO = () => toISODate(new Date());

const prettyDate = (iso) => {
    if (!iso) return '';
    const [y, m, d] = iso.split('-').map(Number);
    const dt = new Date(y, m - 1, d);
    return dt.toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' });
};

// "BHUSAVAL JN (BSL)" → "BHUSAVAL JN", also drops the invisible-char "Airport" suffix
const placeName = (label) =>
    String(label || '').replace(/‎/g, '').split(' (')[0].trim();

const joinList = (arr) => {
    if (arr.length <= 1) return arr.join('');
    return `${arr.slice(0, -1).join(', ')} or ${arr[arr.length - 1]}`;
};

const buildMessage = ({ src, dest, dateFrom, dateTo, modes, trainClass, flightClass }) => {
    const s = placeName(src) || '<source>';
    const d = placeName(dest) || '<destination>';
    const when = dateFrom && dateTo && dateFrom !== dateTo
        ? `from ${prettyDate(dateFrom)} to ${prettyDate(dateTo)}`
        : `on ${prettyDate(dateFrom || dateTo) || '<date>'}`;
    const how = modes.length ? ` by ${joinList(modes.map(m => m.toLowerCase()))}` : '';

    const cls = [];
    if (modes.includes('Flight') && flightClass) cls.push(`${flightClass} for flights`);
    if (modes.includes('Train') && trainClass) cls.push(`${trainClass} for trains`);
    const clsText = cls.length ? `, with preferred seat class ${cls.join(' and ')}` : '';

    return `Please help me get confirmed seats from ${s} to ${d} ${when}${how}${clsText}.`;
};

// Normalise stations + airports once, on first use
let LOCATION_INDEX = null;
const getLocationIndex = () => {
    if (LOCATION_INDEX) return LOCATION_INDEX;
    const stations = (stationsData || [])
        .filter(s => s && s.stationCode && s.stationName)
        .map(s => ({
            type: 'station',
            code: String(s.stationCode).toUpperCase(),
            name: String(s.stationName),
            city: '',
            lat: Number(s.lat || s.lat_long?.[0] || 0),
            lng: Number(s.lng || s.lat_long?.[1] || 0),
        }));
    const airports = (airportsData || [])
        .filter(a => a && a.airportCode && a.airportName)
        .map(a => ({
            type: 'airport',
            code: String(a.airportCode).toUpperCase(),
            name: String(a.airportName),
            city: String(a.city || a.cityName || ''),
            lat: Number(a.lat || a.lat_long?.[0] || 0),
            lng: Number(a.lng || a.lat_long?.[1] || 0),
        }));
    LOCATION_INDEX = [...stations, ...airports].map(item => ({
        ...item,
        _name: item.name.toUpperCase(),
        _city: item.city.toUpperCase(),
        label: `${item.name} (${item.code})`,
    }));
    return LOCATION_INDEX;
};

const searchLocations = (query, limit = 8) => {
    const q = query.trim().toUpperCase();
    if (q.length < 2) return [];
    const scored = [];
    for (const item of getLocationIndex()) {
        let score = -1;
        if (item.code === q) score = 0;
        else if (item._name.startsWith(q) || item._city.startsWith(q)) score = 1;
        else if (item.code.startsWith(q)) score = 2;
        else if (item._name.includes(` ${q}`)) score = 3;
        else if (item._name.includes(q) || item._city.includes(q)) score = 4;
        if (score >= 0) scored.push([score, item]);
    }
    scored.sort((a, b) => a[0] - b[0] || a[1]._name.length - b[1]._name.length);
    return scored.slice(0, limit).map(([, item]) => item);
};

const phoneRule = (code) => COUNTRY_CODES.find(c => c.code === code) || { minLen: 6, maxLen: 14 };

const validatePhone = (code, digits) => {
    if (!digits) return 'Enter your mobile number';
    const { minLen, maxLen } = phoneRule(code);
    if (digits.length < minLen || digits.length > maxLen) {
        return minLen === maxLen
            ? `Enter a ${minLen}-digit mobile number`
            : `Enter a ${minLen}–${maxLen} digit mobile number`;
    }
    if (code === '+91' && !/^[6-9]/.test(digits)) return 'Indian mobile numbers start with 6, 7, 8 or 9';
    return '';
};

// ───────────────────────────────────────────────────────────────────────────
// Small building blocks
// ───────────────────────────────────────────────────────────────────────────
const FieldLabel = ({ htmlFor, children, hint }) => (
    <label htmlFor={htmlFor} className="flex items-baseline justify-between mb-1.5">
        <span className="text-[11px] font-semibold text-slate-500 uppercase tracking-wide">{children}</span>
        {hint && <span className="text-[11px] text-slate-400">{hint}</span>}
    </label>
);

const inputBase =
    'h-11 rounded-xl border bg-white text-base sm:text-sm text-slate-900 placeholder:text-slate-400 ' +
    'focus:outline-none focus:ring-2 focus:ring-orange-400 focus:border-transparent transition-shadow';

const Chip = ({ active, onClick, children, ariaLabel }) => (
    <button
        type="button"
        onClick={onClick}
        aria-pressed={active}
        aria-label={ariaLabel}
        className={`inline-flex items-center gap-1.5 min-h-[36px] px-3 rounded-full border text-sm font-medium transition-colors
            ${active
                ? 'bg-orange-500 border-orange-500 text-white'
                : 'bg-white border-slate-200 text-slate-600 hover:border-orange-300 hover:text-slate-800'}`}
    >
        {children}
    </button>
);

// Station / airport autocomplete (same data the search form uses for smart resolution)
const LocationField = ({ id, label, value, onChange, placeholder }) => {
    const [open, setOpen] = useState(false);
    const [highlight, setHighlight] = useState(0);
    const wrapRef = useRef(null);
    const listId = `${id}-list`;

    const results = useMemo(() => (open ? searchLocations(value.text) : []), [open, value.text]);

    useEffect(() => { setHighlight(0); }, [value.text]);

    useEffect(() => {
        const onDoc = (e) => {
            if (wrapRef.current && !wrapRef.current.contains(e.target)) setOpen(false);
        };
        document.addEventListener('mousedown', onDoc);
        document.addEventListener('touchstart', onDoc, { passive: true });
        return () => {
            document.removeEventListener('mousedown', onDoc);
            document.removeEventListener('touchstart', onDoc);
        };
    }, []);

    const pick = (item) => {
        onChange({ text: item.label, data: { code: item.code, label: item.label, type: item.type, lat: item.lat, lng: item.lng } });
        setOpen(false);
    };

    const onKeyDown = (e) => {
        if (!open || results.length === 0) return;
        if (e.key === 'ArrowDown') { e.preventDefault(); setHighlight(h => (h + 1) % results.length); }
        else if (e.key === 'ArrowUp') { e.preventDefault(); setHighlight(h => (h - 1 + results.length) % results.length); }
        else if (e.key === 'Enter') { e.preventDefault(); pick(results[highlight]); }
        else if (e.key === 'Escape') { e.stopPropagation(); setOpen(false); }
    };

    return (
        <div ref={wrapRef} className="relative">
            <FieldLabel htmlFor={id}>{label}</FieldLabel>
            <div className="relative">
                <MapPin size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400 pointer-events-none" aria-hidden="true" />
                <input
                    id={id}
                    type="text"
                    autoComplete="off"
                    role="combobox"
                    aria-expanded={open && results.length > 0}
                    aria-controls={listId}
                    aria-autocomplete="list"
                    value={value.text}
                    placeholder={placeholder}
                    onChange={(e) => { onChange({ text: e.target.value, data: null }); setOpen(true); }}
                    onFocus={(e) => { e.target.select(); setOpen(true); }}
                    onKeyDown={onKeyDown}
                    className={`${inputBase} w-full border-slate-200 pl-9 pr-9`}
                />
                {value.text && (
                    <button
                        type="button"
                        onClick={() => { onChange({ text: '', data: null }); setOpen(false); }}
                        className="absolute right-2 top-1/2 -translate-y-1/2 w-7 h-7 flex items-center justify-center rounded-full text-slate-400 hover:text-slate-600 hover:bg-slate-100"
                        aria-label={`Clear ${label}`}
                    >
                        <X size={14} />
                    </button>
                )}
            </div>

            {open && results.length > 0 && (
                <ul
                    id={listId}
                    role="listbox"
                    className="absolute z-20 left-0 right-0 mt-1 max-h-60 overflow-y-auto rounded-xl border border-slate-200 bg-white shadow-xl py-1"
                >
                    {results.map((item, i) => (
                        <li
                            key={`${item.type}-${item.code}`}
                            role="option"
                            aria-selected={i === highlight}
                            onMouseDown={(e) => e.preventDefault()}
                            onClick={() => pick(item)}
                            onMouseEnter={() => setHighlight(i)}
                            className={`flex items-center gap-2.5 px-3 py-2.5 cursor-pointer text-sm ${i === highlight ? 'bg-orange-50' : ''}`}
                        >
                            {item.type === 'airport'
                                ? <Plane size={15} className="shrink-0 text-slate-400" aria-hidden="true" />
                                : <Train size={15} className="shrink-0 text-slate-400" aria-hidden="true" />}
                            <span className="min-w-0 flex-1 truncate text-slate-800">{item.name}</span>
                            <span className="shrink-0 text-xs font-semibold text-slate-500">{item.code}</span>
                        </li>
                    ))}
                </ul>
            )}
        </div>
    );
};

// ───────────────────────────────────────────────────────────────────────────
// Success view
// ───────────────────────────────────────────────────────────────────────────
const SuccessView = ({ action, phoneText, onClose, supportPhone, supportPhoneDisplay, callbackWhen }) => {
    const [copied, setCopied] = useState(false);
    const copy = async () => {
        try {
            await navigator.clipboard.writeText(supportPhone);
            setCopied(true);
            setTimeout(() => setCopied(false), 1500);
        } catch { /* clipboard blocked */ }
    };

    const isCallback = action === 'callback_request';

    return (
        <div className="flex flex-col items-center text-center px-2 py-8 sm:py-10">
            <div className="w-16 h-16 rounded-full bg-green-50 text-green-600 flex items-center justify-center">
                <CheckCircle2 size={34} aria-hidden="true" />
            </div>
            <h3 className="mt-4 text-lg font-semibold text-slate-900">
                {isCallback ? 'Callback requested' : 'Call us now'}
            </h3>
            <p className="mt-1.5 text-sm text-slate-500 max-w-xs">
                {isCallback
                    ? <>Our travel team will call you on <span className="font-medium text-slate-700">{phoneText}</span> {callbackWhen}.</>
                    : <>If your dialer didn't open, call us on the number below. We've saved your trip details.</>}
            </p>

            {!isCallback && (
                <div className="mt-5 flex items-center gap-2">
                    <a
                        href={`tel:${supportPhone}`}
                        className="inline-flex items-center gap-2 min-h-[44px] px-4 rounded-xl bg-orange-50 text-orange-700 font-semibold text-base"
                    >
                        <Phone size={16} aria-hidden="true" /> {supportPhoneDisplay}
                    </a>
                    <button
                        type="button"
                        onClick={copy}
                        className="w-11 h-11 flex items-center justify-center rounded-xl border border-slate-200 text-slate-500 hover:bg-slate-50"
                        aria-label="Copy phone number"
                    >
                        {copied ? <Check size={16} className="text-green-600" /> : <Copy size={16} />}
                    </button>
                </div>
            )}

            <button
                type="button"
                onClick={onClose}
                className="mt-6 min-h-[44px] px-8 rounded-xl bg-slate-900 hover:bg-slate-800 text-white font-semibold text-sm"
            >
                Done
            </button>
        </div>
    );
};

// ───────────────────────────────────────────────────────────────────────────
// ContactModal
// ───────────────────────────────────────────────────────────────────────────
/**
 * props:
 *   isOpen, onClose
 *   onSubmit(leadAction, formData) => Promise<boolean>
 *   prefill: { src_name, src_data, dest_name, dest_data, travel_date_from, travel_date_to,
 *              modes, seat_classes }
 *   callUs: config.call_us from intent "contact_lead_config" (null → callback only)
 *   supportPhone / supportPhoneDisplay: number the "Call us" button dials
 */
const ContactModal = ({
    isOpen,
    onClose,
    onSubmit,
    prefill,
    callUs = null,
    supportPhone = SUPPORT_PHONE,
    supportPhoneDisplay = SUPPORT_PHONE_DISPLAY,
}) => {
    // (not React.useId, so this also works on React 17)
    const uid = useRef(Math.random().toString(36).slice(2, 8)).current;
    const titleId = `cm-title-${uid}`;
    const sheetRef = useRef(null);

    const [src, setSrc] = useState({ text: '', data: null });
    const [dest, setDest] = useState({ text: '', data: null });
    const [dateFrom, setDateFrom] = useState('');
    const [dateTo, setDateTo] = useState('');
    const [modes, setModes] = useState([]);
    const [trainClass, setTrainClass] = useState('');
    const [flightClass, setFlightClass] = useState('');
    const [countryCode, setCountryCode] = useState(DEFAULT_COUNTRY_CODE);
    const [phone, setPhone] = useState('');
    const [message, setMessage] = useState('');
    const [messageEdited, setMessageEdited] = useState(false);

    const [phoneError, setPhoneError] = useState('');
    const [submitError, setSubmitError] = useState('');
    const [busyAction, setBusyAction] = useState(null);
    const [doneAction, setDoneAction] = useState(null);

    // "Call us" is only offered inside call hours; re-checked every 30s while open
    const [tick, setTick] = useState(0);
    useEffect(() => {
        if (!isOpen) return undefined;
        const id = setInterval(() => setTick(t => t + 1), 30000);
        return () => clearInterval(id);
    }, [isOpen]);
    const callStatus = useMemo(() => getCallStatus(callUs), [callUs, tick, isOpen]); // eslint-disable-line react-hooks/exhaustive-deps
    const callUsOpen = callStatus.open;
    const nextOpenText = describeNextOpen(callStatus.nextOpen, callUs);

    // Prefill every time the modal opens
    useEffect(() => {
        if (!isOpen) return;
        const p = prefill || {};
        const today = todayISO();
        const from = p.travel_date_from && p.travel_date_from >= today ? p.travel_date_from : today;
        const to = p.travel_date_to && p.travel_date_to >= from ? p.travel_date_to : from;

        const rawModes = Array.isArray(p.modes) ? p.modes : [];
        const normModes = rawModes.includes('All')
            ? [...MODE_OPTIONS]
            : MODE_OPTIONS.filter(m => rawModes.includes(m));

        const classes = Array.isArray(p.seat_classes) ? p.seat_classes : [];

        // Keep the resolved code even when the search used a typed name (no location object)
        const withCode = (data, code, name) =>
            data?.code ? data : (code ? { ...(data || {}), code, label: name } : (data || null));
        setSrc({ text: p.src_name || '', data: withCode(p.src_data, p.src_code, p.src_name) });
        setDest({ text: p.dest_name || '', data: withCode(p.dest_data, p.dest_code, p.dest_name) });
        setDateFrom(from);
        setDateTo(to);
        setModes(normModes.length ? normModes : ['Train']);
        setTrainClass(classes.find(c => TRAIN_CLASS_VALUES.includes(c)) || '');
        setFlightClass(classes.find(c => FLIGHT_CLASS_VALUES.includes(c)) || '');
        setCountryCode(DEFAULT_COUNTRY_CODE);
        setPhone('');
        setMessageEdited(false);
        setPhoneError('');
        setSubmitError('');
        setBusyAction(null);
        setDoneAction(null);
    }, [isOpen]); // eslint-disable-line react-hooks/exhaustive-deps

    // Auto-message follows the fields until the user edits it
    useEffect(() => {
        if (messageEdited) return;
        setMessage(buildMessage({
            src: src.text, dest: dest.text, dateFrom, dateTo, modes, trainClass, flightClass,
        }));
    }, [src.text, dest.text, dateFrom, dateTo, modes, trainClass, flightClass, messageEdited]);

    // Scroll lock + Esc + initial focus
    useEffect(() => {
        if (!isOpen) return undefined;
        const prevOverflow = document.body.style.overflow;
        document.body.style.overflow = 'hidden';
        const onKey = (e) => { if (e.key === 'Escape' && !busyAction) onClose(); };
        document.addEventListener('keydown', onKey);
        const t = setTimeout(() => sheetRef.current?.focus(), 0);
        return () => {
            document.body.style.overflow = prevOverflow;
            document.removeEventListener('keydown', onKey);
            clearTimeout(t);
        };
    }, [isOpen, onClose, busyAction]);

    const toggleMode = (m) =>
        setModes(prev => (prev.includes(m) ? prev.filter(x => x !== m) : MODE_OPTIONS.filter(x => x === m || prev.includes(x))));

    const swap = () => { setSrc(dest); setDest(src); };

    const onDateFromChange = (v) => {
        setDateFrom(v);
        if (!dateTo || v > dateTo) setDateTo(v);
    };

    const phoneDigits = phone.replace(/\D/g, '');
    const phoneText = `${countryCode} ${phoneDigits}`;

    const formData = useCallback(() => ({
        src_name: src.text,
        src_code: src.data?.code || '',
        dest_name: dest.text,
        dest_code: dest.data?.code || '',
        travel_date_from: dateFrom,
        travel_date_to: dateTo,
        modes,
        seat_classes: [
            ...(modes.includes('Train') && trainClass ? [trainClass] : []),
            ...(modes.includes('Flight') && flightClass ? [flightClass] : []),
        ],
        phone: phoneDigits || null,
        country_code: countryCode,
        message: message.trim(),
    }), [src, dest, dateFrom, dateTo, modes, trainClass, flightClass, phoneDigits, countryCode, message]);

    const handleCallUs = async () => {
        // Hours may have ended since the popup opened
        if (!getCallStatus(callUs).open) { setTick(t => t + 1); return; }
        // Phone is optional here, but if they typed one it should be valid
        if (phoneDigits) {
            const err = validatePhone(countryCode, phoneDigits);
            if (err) { setPhoneError(err); return; }
        }
        setSubmitError('');
        setBusyAction('call_us');
        // Log first (capped so the dialer never waits long), then open the dialer
        await Promise.race([
            onSubmit('call_us', formData()),
            new Promise(resolve => setTimeout(resolve, 1500)),
        ]);
        setBusyAction(null);
        setDoneAction('call_us');
        window.location.href = `tel:${supportPhone}`;
    };

    const handleCallback = async () => {
        const err = validatePhone(countryCode, phoneDigits);
        if (err) { setPhoneError(err); return; }
        setSubmitError('');
        setBusyAction('callback_request');
        const ok = await onSubmit('callback_request', formData());
        setBusyAction(null);
        if (ok) {
            setDoneAction('callback_request');
        } else {
            setSubmitError(callUsOpen
                ? `We couldn't send your request. Please try again, or call us on ${supportPhoneDisplay}.`
                : "We couldn't send your request. Please check your connection and try again.");
        }
    };

    if (!FEATURES.ENABLE_CONTACT_FORM || !isOpen || typeof document === 'undefined') return null;

    const showTrainClass = modes.includes('Train');
    const showFlightClass = modes.includes('Flight');
    const minDate = todayISO();

    return createPortal(
        <div className="fixed inset-0 z-[1000] flex items-end sm:items-center justify-center sm:p-4">
            {/* Backdrop */}
            <div
                className="absolute inset-0 bg-slate-900/55 backdrop-blur-[2px]"
                onClick={() => { if (!busyAction) onClose(); }}
                aria-hidden="true"
            />

            {/* Sheet: bottom sheet on mobile, centred card from sm up */}
            <div
                ref={sheetRef}
                tabIndex={-1}
                role="dialog"
                aria-modal="true"
                aria-labelledby={titleId}
                className="relative w-full sm:max-w-lg bg-white rounded-t-3xl sm:rounded-2xl shadow-2xl
                           flex flex-col max-h-[94dvh] sm:max-h-[88vh] outline-none
                           animate-[cmSlideUp_.22s_ease-out] sm:animate-none"
            >
                <style>{`@keyframes cmSlideUp{from{transform:translateY(24px);opacity:.6}to{transform:none;opacity:1}}`}</style>

                {/* Grab handle (mobile) */}
                <div className="sm:hidden flex justify-center pt-2.5" aria-hidden="true">
                    <span className="w-10 h-1 rounded-full bg-slate-200" />
                </div>

                {/* Header */}
                <div className="shrink-0 flex items-start gap-3 px-5 pt-3 sm:pt-5 pb-4 border-b border-slate-100">
                    <div className="shrink-0 w-10 h-10 rounded-xl bg-orange-50 text-orange-600 flex items-center justify-center" aria-hidden="true">
                        <PhoneCall size={19} />
                    </div>
                    <div className="min-w-0 flex-1">
                        <h2 id={titleId} className="text-base sm:text-lg font-semibold text-slate-900 leading-snug">
                            Get help with confirmed tickets
                        </h2>
                        <p className="text-xs sm:text-sm text-slate-500 mt-0.5">
                            Check your trip details and our team will help you find seats.
                        </p>
                    </div>
                    <button
                        type="button"
                        onClick={onClose}
                        disabled={!!busyAction}
                        className="shrink-0 -mr-1 w-9 h-9 flex items-center justify-center rounded-full text-slate-400 hover:text-slate-700 hover:bg-slate-100 disabled:opacity-40"
                        aria-label="Close"
                    >
                        <X size={18} />
                    </button>
                </div>

                {doneAction ? (
                    <div className="overflow-y-auto px-5 pb-[max(1rem,env(safe-area-inset-bottom))]">
                        <SuccessView
                            action={doneAction}
                            phoneText={phoneText}
                            onClose={onClose}
                            supportPhone={supportPhone}
                            supportPhoneDisplay={supportPhoneDisplay}
                            callbackWhen={callUsOpen || !nextOpenText ? 'shortly' : `when our lines open ${nextOpenText}`}
                        />
                    </div>
                ) : (
                    <>
                        {/* Body */}
                        <div className="flex-1 overflow-y-auto overscroll-contain px-5 py-5 space-y-5">
                            {/* From / To with swap */}
                            <div className="relative grid grid-cols-1 sm:grid-cols-2 gap-3 sm:gap-4">
                                <LocationField
                                    id={`cm-src-${uid}`}
                                    label="From"
                                    value={src}
                                    onChange={setSrc}
                                    placeholder="Station, airport or city"
                                />
                                <LocationField
                                    id={`cm-dest-${uid}`}
                                    label="To"
                                    value={dest}
                                    onChange={setDest}
                                    placeholder="Station, airport or city"
                                />
                                <button
                                    type="button"
                                    onClick={swap}
                                    className="absolute right-12 top-[56px] sm:right-auto sm:left-1/2 sm:-translate-x-1/2 sm:top-[28px]
                                               z-10 w-8 h-8 flex items-center justify-center rounded-full border border-slate-200 bg-white
                                               text-slate-500 hover:text-orange-600 hover:border-orange-300 shadow-sm"
                                    aria-label="Swap source and destination"
                                >
                                    <ArrowUpDown size={14} className="sm:rotate-90" />
                                </button>
                            </div>

                            {/* Date range */}
                            <div>
                                <FieldLabel htmlFor={`cm-df-${uid}`} hint="Flexible? Widen the range">Travel dates</FieldLabel>
                                <div className="grid grid-cols-2 gap-3">
                                    <div className="relative">
                                        <Calendar size={15} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400 pointer-events-none" aria-hidden="true" />
                                        <input
                                            id={`cm-df-${uid}`}
                                            type="date"
                                            aria-label="Earliest travel date"
                                            value={dateFrom}
                                            min={minDate}
                                            onChange={(e) => onDateFromChange(e.target.value)}
                                            className={`${inputBase} w-full border-slate-200 pl-9 pr-2`}
                                        />
                                    </div>
                                    <div className="relative">
                                        <Calendar size={15} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400 pointer-events-none" aria-hidden="true" />
                                        <input
                                            type="date"
                                            aria-label="Latest travel date"
                                            value={dateTo}
                                            min={dateFrom || minDate}
                                            onChange={(e) => setDateTo(e.target.value)}
                                            className={`${inputBase} w-full border-slate-200 pl-9 pr-2`}
                                        />
                                    </div>
                                </div>
                            </div>

                            {/* Modes */}
                            <div>
                                <FieldLabel>Mode of travel</FieldLabel>
                                <div className="flex flex-wrap gap-2">
                                    {MODE_OPTIONS.map(m => {
                                        const Icon = MODE_ICONS[m];
                                        return (
                                            <Chip key={m} active={modes.includes(m)} onClick={() => toggleMode(m)}>
                                                <Icon size={14} aria-hidden="true" /> {m}
                                            </Chip>
                                        );
                                    })}
                                </div>
                            </div>

                            {/* Classes */}
                            {(showTrainClass || showFlightClass) && (
                                <div className="space-y-3">
                                    {showTrainClass && (
                                        <div>
                                            <FieldLabel>Train class</FieldLabel>
                                            <div className="flex gap-2 overflow-x-auto scrollbar-hide -mx-1 px-1 pb-0.5 sm:flex-wrap sm:overflow-visible">
                                                {TRAIN_CLASS_OPTIONS.map(o => (
                                                    <Chip
                                                        key={o.value}
                                                        active={trainClass === o.value}
                                                        onClick={() => setTrainClass(prev => (prev === o.value ? '' : o.value))}
                                                    >
                                                        <span className="whitespace-nowrap">{o.label}</span>
                                                    </Chip>
                                                ))}
                                            </div>
                                        </div>
                                    )}
                                    {showFlightClass && (
                                        <div>
                                            <FieldLabel>Flight class</FieldLabel>
                                            <div className="flex gap-2 overflow-x-auto scrollbar-hide -mx-1 px-1 pb-0.5 sm:flex-wrap sm:overflow-visible">
                                                {FLIGHT_CLASS_OPTIONS.map(o => (
                                                    <Chip
                                                        key={o.value}
                                                        active={flightClass === o.value}
                                                        onClick={() => setFlightClass(prev => (prev === o.value ? '' : o.value))}
                                                    >
                                                        <span className="whitespace-nowrap">{o.label}</span>
                                                    </Chip>
                                                ))}
                                            </div>
                                        </div>
                                    )}
                                </div>
                            )}

                            {/* Phone */}
                            <div>
                                <FieldLabel htmlFor={`cm-ph-${uid}`} hint="Needed for a callback">Mobile number</FieldLabel>
                                <div className="flex gap-2">
                                    <select
                                        value={countryCode}
                                        onChange={(e) => { setCountryCode(e.target.value); setPhoneError(''); }}
                                        aria-label="Country code"
                                        className={`${inputBase} border-slate-200 w-[104px] shrink-0 pl-2.5 pr-1`}
                                    >
                                        {COUNTRY_CODES.map(c => (
                                            <option key={c.iso} value={c.code}>{c.flag} {c.code}</option>
                                        ))}
                                    </select>
                                    <input
                                        id={`cm-ph-${uid}`}
                                        type="tel"
                                        inputMode="numeric"
                                        autoComplete="tel-national"
                                        placeholder={countryCode === '+91' ? '98765 43210' : 'Mobile number'}
                                        value={phone}
                                        maxLength={18}
                                        onChange={(e) => { setPhone(e.target.value.replace(/[^\d\s]/g, '')); setPhoneError(''); }}
                                        aria-invalid={!!phoneError}
                                        aria-describedby={phoneError ? `cm-ph-err-${uid}` : undefined}
                                        className={`${inputBase} flex-1 min-w-0 px-3 ${phoneError ? 'border-red-400 focus:ring-red-400' : 'border-slate-200'}`}
                                    />
                                </div>
                                {phoneError && (
                                    <p id={`cm-ph-err-${uid}`} className="mt-1.5 text-xs text-red-600">{phoneError}</p>
                                )}
                            </div>

                            {/* Message */}
                            <div>
                                <div className="flex items-baseline justify-between mb-1.5">
                                    <label htmlFor={`cm-msg-${uid}`} className="text-[11px] font-semibold text-slate-500 uppercase tracking-wide">
                                        Message <span className="normal-case font-normal text-slate-400">(optional)</span>
                                    </label>
                                    {messageEdited && (
                                        <button
                                            type="button"
                                            onClick={() => setMessageEdited(false)}
                                            className="inline-flex items-center gap-1 text-xs text-orange-600 hover:underline"
                                        >
                                            <RotateCcw size={11} aria-hidden="true" /> Reset
                                        </button>
                                    )}
                                </div>
                                <textarea
                                    id={`cm-msg-${uid}`}
                                    rows={3}
                                    value={message}
                                    onChange={(e) => { setMessage(e.target.value); setMessageEdited(true); }}
                                    className="w-full rounded-xl border border-slate-200 bg-white px-3 py-2.5 text-base sm:text-sm text-slate-800
                                               leading-relaxed resize-none focus:outline-none focus:ring-2 focus:ring-orange-400 focus:border-transparent"
                                />
                            </div>

                            {submitError && (
                                <div className="flex items-start gap-2 rounded-xl bg-red-50 border border-red-100 px-3 py-2.5 text-sm text-red-700" role="alert">
                                    <AlertCircle size={16} className="shrink-0 mt-0.5" aria-hidden="true" />
                                    <span>{submitError}</span>
                                </div>
                            )}
                        </div>

                        {/* Footer — sticky, respects the iPhone home indicator */}
                        <div className="shrink-0 border-t border-slate-100 bg-white rounded-b-2xl px-5 pt-3
                                        pb-[max(0.75rem,env(safe-area-inset-bottom))] sm:pb-4">
                        <p className="flex items-start gap-1.5 text-xs text-slate-500 mb-2.5" aria-live="polite">
                            <Clock size={13} className="shrink-0 mt-px" aria-hidden="true" />
                            {callUsOpen
                                ? <span>Phone lines open{callStatus.closes ? ` till ${formatCloseTime(callStatus.closes.minutes)}` : ''}. Call now or ask for a callback.</span>
                                : <span>Our phone lines are closed right now{nextOpenText ? ` (we open ${nextOpenText})` : ''}. Leave your number and we'll call you back.</span>}
                        </p>
                        <div className={`grid gap-3 ${callUsOpen ? 'grid-cols-2' : 'grid-cols-1'}`}>
                            {callUsOpen && (
                            <button
                                type="button"
                                onClick={handleCallUs}
                                disabled={!!busyAction}
                                className="inline-flex items-center justify-center gap-2 min-h-[48px] px-3 rounded-xl
                                           border-2 border-orange-500 text-orange-600 font-semibold text-sm
                                           hover:bg-orange-50 active:bg-orange-100 transition-colors
                                           disabled:opacity-60 disabled:cursor-not-allowed
                                           focus:outline-none focus-visible:ring-2 focus-visible:ring-orange-400 focus-visible:ring-offset-1"
                            >
                                {busyAction === 'call_us'
                                    ? <Loader2 size={17} className="animate-spin" aria-hidden="true" />
                                    : <Phone size={17} aria-hidden="true" />}
                                Call us
                            </button>
                            )}
                            <button
                                type="button"
                                onClick={handleCallback}
                                disabled={!!busyAction}
                                className="inline-flex items-center justify-center gap-2 min-h-[48px] px-3 rounded-xl
                                           bg-orange-500 hover:bg-orange-600 active:bg-orange-700 text-white font-semibold text-sm
                                           shadow-md shadow-orange-500/30 transition-colors
                                           disabled:opacity-60 disabled:cursor-not-allowed
                                           focus:outline-none focus-visible:ring-2 focus-visible:ring-orange-400 focus-visible:ring-offset-1"
                            >
                                {busyAction === 'callback_request'
                                    ? <Loader2 size={17} className="animate-spin" aria-hidden="true" />
                                    : <PhoneCall size={17} aria-hidden="true" />}
                                <span className="whitespace-nowrap">Request a callback</span>
                            </button>
                        </div>
                        </div>
                    </>
                )}
            </div>
        </div>,
        document.body
    );
};

export default ContactModal;
